import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Modal } from 'antd';

import ReportContext from '@totem/components/billingRates/BillingRateReportContext';
import {
  BillingRate,
  BillingRatesReport,
} from '@totem/components/billingRates/dataTypes';
import { ReportInput } from '@totem/components/billingRates/parameterTypes';
import { buildReportFilters } from '@totem/components/billingRates/utilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { BILLING_RATES_ENDPOINT } from '@totem/utilities/endpoints';
import { omitNilOrEmpty } from '@totem/utilities/objectUtilities';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
const confirm = Modal.confirm;
type Props = {
  refresh?: boolean;
  onRecordTotalChanged?: (total: number) => void;
  onDataRefreshRequested?: () => void;
  children?: ReactNode;
  deviceEndPoint?: string;
};
const BillingRateReportContainer = ({
  children,
  refresh,
  onRecordTotalChanged,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [input, updateInput] = useState<ReportInput>({
    pageSize: 10,
    page: 1,
    sortField: 'name',
    sortDirection: '1',
    name: '',
  });
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [reportData, setReportData] = useState<BillingRatesReport>(null);
  const [isSending, setIsSending] = useState<boolean>(false);
  const setInput = (updated: Partial<ReportInput>) => {
    updateInput(omitNilOrEmpty({ ...input, ...updated }));

    setRefreshData(true);
  };
  const sendDeleteBillingRate = useCallback(
    async (billingRateId: string) => {
      if (isSending) {
        return;
      }
      setIsSending(true);
      const endPoint = `${BILLING_RATES_ENDPOINT}/${billingRateId}`;
      fetch(endPoint, {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      }).then((res) => {
        CheckResponseShowError(res);

        setIsSending(false);

        setRefreshData(true);
      });
    },
    [isSending],
  );
  const buildParameters = () => {
    const params: Params = {
      paging: {
        page: input.page,
        pageSize: input.pageSize,
      },
      sort: {
        field: input.sortField,
        direction: +input.sortDirection,
      },
      filters: buildReportFilters(input),
    };
    return params;
  };
  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      setIsLoading(true);
      const params: Params = buildParameters();
      fetch(`${BILLING_RATES_ENDPOINT}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
      })
        .then((res) => res.json())
        .then((result: BillingRatesReport) => {
          setReportData(result);

          setTotalRecords(result.paging.totalRecords);
          if (
            typeof onRecordTotalChanged !== 'undefined' &&
            onRecordTotalChanged !== null
          ) {
            onRecordTotalChanged(result.paging.totalRecords);
          }
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, refresh, getApiToken]);
  const handleAction = (newAction: string, actionItem: BillingRate) => {
    switch (newAction) {
      case 'refresh':
        setRefreshData(true);
        break;
      case 'delete':
        confirm({
          title: 'Are you sure?',
          content: `Please confirm that would like to delete this billing rate entry for ${actionItem.name}.`,
          okText: 'Delete',
          okType: 'danger',
          cancelText: 'Cancel',
          onOk: () => {
            sendDeleteBillingRate(actionItem.id);
          },
        });
        break;
      default:
        break;
    }
  };
  return (
    <ReportContext.Provider
      value={{
        input,
        setInput,
        onAction: handleAction,
        loading: isLoading,
        reportData,
        totalRecords,
      }}
    >
      <div>{children}</div>
    </ReportContext.Provider>
  );
};
export default BillingRateReportContainer;
